import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { useCallback, useMemo, useRef } from 'react';
import { 
    resetTransactions, 
    addTransaction, 
    addOrReplaceTransaction,
    markTransactionAsRead, 
    markTransactionAsAnnounced,
    markAllTransactionsAsRead,
    fetchSparkTransactions,
    fetchInternalTransactions,
    fetchPendingDeposits
} from '../store/slices/walletTransactionsSlice';
import {
    getSearchTransactionByPaymentHash,
    resetSearchTransactions,
} from '../store/slices/walletTransactionSearchSlice';
import { Transaction, WalletType, PaymentType, TransactionStatus } from '../models/Transactions';
import { walletRepository } from '../repositories/WalletRepository';
import { getSparkPayment, listLocalBreezPayments, getPaymentsFromServer } from '../utils/BreezSpark';
import { countUnreadTransactions } from '../utils/transactionDisplay';

export function useWalletTransactions() {
    const dispatch = useDispatch<AppDispatch>();
    const walletTransactions = useSelector((state: RootState) => state.walletTransactions);
    const walletType = useSelector((state: RootState) => state.wallet.walletType);
    const currentUserId = useSelector((state: RootState) => state.auth.currentUserId);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const filteredTransactions = useMemo(() => {
        return walletTransactions.transactions.filter(
            transaction => transaction.user === currentUserId || transaction.user === undefined
        );
    }, [walletTransactions.transactions, currentUserId]);

    const filteredPendingTransactions = useMemo(() => {
        return walletTransactions.pendingTransactions.filter(
            transaction => transaction.user === currentUserId || transaction.user === undefined
        );
    }, [walletTransactions.pendingTransactions, currentUserId]);

    const walletTransactionSearch = useSelector((state: RootState) => state.walletTransactionSearch);

    const filteredSearchTransactions = useMemo(() => {
        return walletTransactionSearch.transactions.filter(
            transaction => transaction.user === currentUserId || transaction.user === undefined
        );
    }, [walletTransactionSearch.transactions, currentUserId]);

    const loadTransactions = useCallback((perPage: number = 40) => {
        switch(walletType) {
            case WalletType.SPARK:
                if(walletTransactions.hasMoreSpark) {
                    if(!walletTransactions.isLoadingSpark) {
                        dispatch(fetchSparkTransactions({ page: walletTransactions.currentPageSpark, perPage }));
                    }
                } else if(walletTransactions.hasMoreInternal) {
                    if(!walletTransactions.isLoadingInternal) {
                        dispatch(fetchInternalTransactions({ page: walletTransactions.currentPageInternal, perPage }));
                    }
                }
                break;
            case WalletType.INTERNAL:
                if(walletTransactions.hasMoreInternal && !walletTransactions.isLoadingInternal) {
                    dispatch(fetchInternalTransactions({ page: walletTransactions.currentPageInternal, perPage }));
                }
                break;
        }
    }, [dispatch, walletType, walletTransactions.hasMoreSpark, walletTransactions.isLoadingSpark, walletTransactions.currentPageSpark, walletTransactions.hasMoreInternal, walletTransactions.isLoadingInternal, walletTransactions.currentPageInternal]);

    const markTransactionAsReadHook = useCallback((transaction: Transaction) => {
        if(transaction.walletType === WalletType.SPARK) {
            walletRepository.markSparkPaymentAsRead(transaction.id);
            dispatch(markTransactionAsRead(transaction.id));
        } else {
            walletRepository.markTransactionAsRead(transaction.paymentHash);
            dispatch(markTransactionAsRead(transaction.paymentHash));
        }
    }, [dispatch]);

    const markTransactionAsAnnouncedHook = useCallback((transaction: Transaction) => {
        if(transaction.walletType === WalletType.SPARK) {
            walletRepository.markSparkPaymentAsAnnounced(transaction.id);
            dispatch(markTransactionAsAnnounced(transaction.id));
        } else {
            walletRepository.markTransactionAsAnnounced(transaction.paymentHash);
            dispatch(markTransactionAsAnnounced(transaction.paymentHash));
        }
    }, [dispatch]);

    const markAllTransactionsAsReadHook = useCallback(async () => {
        if (walletType === WalletType.SPARK) {
            await walletRepository.markAllSparkPaymentsAsRead();
        }
        dispatch(markAllTransactionsAsRead());
    }, [dispatch, walletType]);

    const getTransaction = useCallback(async (paymentHash: string): Promise<Transaction | null> => {
        const existingTransaction = filteredTransactions.find(t => t.paymentHash === paymentHash);

        if (existingTransaction) {
            return existingTransaction;
        }

        const pendingTransaction = filteredPendingTransactions.find(t => t.paymentHash === paymentHash);
        if (pendingTransaction) {
            return pendingTransaction;
        }

        try {
            return await refreshTransaction(paymentHash);
        } catch (error) {
            return null;
        }
    }, [filteredTransactions, dispatch]);

    const refreshTransaction = useCallback(async (paymentHash: string): Promise<Transaction | null> => {
        let transaction: Transaction | null = null;

        switch(walletType) {
            case WalletType.SPARK:
                transaction = await getSparkPayment(paymentHash);
                if(transaction) {
                    dispatch(addOrReplaceTransaction(transaction));
                }
                break;
            case WalletType.INTERNAL:
                transaction = await walletRepository.getTransaction(paymentHash);
                if(transaction) {
                    dispatch(addOrReplaceTransaction(transaction));
                }
                break;
        }

        return transaction;
    }, [dispatch]);

    const getSearchTransaction = useCallback(async (paymentHash: string): Promise<Transaction | null> => {
        const existingTransaction = filteredSearchTransactions.find(t => t.paymentHash === paymentHash);
        if (existingTransaction) {
            return existingTransaction;
        }

        const resultAction = await dispatch(getSearchTransactionByPaymentHash(paymentHash));
        if (getSearchTransactionByPaymentHash.fulfilled.match(resultAction)) {
            return resultAction.payload;
        }

        return null;
    }, [filteredSearchTransactions, dispatch]);

    const nbTxUnread = useMemo((): number => {
        return countUnreadTransactions(filteredTransactions);
    }, [filteredTransactions]);

    const cleanupPendingTransactions = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const refreshLastSparkTransactions = useCallback(async () => {
        const payments = await listLocalBreezPayments({
            offset: 0,
            limit: 20,
        });

        const currentTransactions = filteredTransactions;
        const existingTransactionIds = new Set(currentTransactions.map((transaction) => transaction.paymentHash));
        const newPayments = payments.filter((payment) => !existingTransactionIds.has(payment.id));
        
        const pendingTransactions = currentTransactions.filter(
            (tx: Transaction) => tx.status === TransactionStatus.PENDING
        );
        const pendingPaymentHashes = pendingTransactions.map(tx => tx.paymentHash);
        
        const paymentsToRefresh = payments.filter((payment) => 
            pendingPaymentHashes.includes(payment.id)
        );
        
        const allPaymentsToLoad = [...newPayments, ...paymentsToRefresh];
        
        if(allPaymentsToLoad.length > 0) {
            const transactions = await getPaymentsFromServer(allPaymentsToLoad);

            transactions.forEach((transaction) => {
                dispatch(addOrReplaceTransaction(transaction));
            });
        }
    }, [filteredTransactions, dispatch]);

    const refreshPendingDeposits = useCallback(async () => {
        if (walletType === WalletType.SPARK) {
            dispatch(fetchPendingDeposits());
        }
    }, [dispatch, walletType]);

    const refreshLastTransactions = useCallback(async () => {
        switch(walletType) {
            case WalletType.SPARK:
                refreshLastSparkTransactions();
                refreshPendingDeposits();
                break;
            case WalletType.INTERNAL:
                walletRepository.getTransactions(20, 1)
                    .then((transactions) => {
                        transactions.forEach((transaction) => {
                            dispatch(addOrReplaceTransaction(transaction));
                        });
                    })
                    .catch((error) => {
                        console.error('Error refreshing last transactions', error);
                    });
                break;
        }
    }, [walletType, refreshLastSparkTransactions, refreshPendingDeposits]);

    const refreshPendingTransactions = useCallback(() => {
        cleanupPendingTransactions();

        const pendingTransactions = filteredTransactions.filter(
            tx => tx.type === PaymentType.bulk && tx.status === TransactionStatus.PENDING
        );
        
        if (pendingTransactions.length > 0) {
            intervalRef.current = setInterval(() => {
                pendingTransactions.forEach(tx => {
                    refreshTransaction(tx.paymentHash);
                });
            }, 5000);
        }
    }, [filteredTransactions, cleanupPendingTransactions, refreshTransaction]);

    const unsetWalletTransactions = useCallback(() => {
        cleanupPendingTransactions();
        dispatch(resetTransactions());
        dispatch(resetSearchTransactions());
    }, [cleanupPendingTransactions, dispatch]);

    const isLoading = useMemo(() => {
        return walletType === WalletType.SPARK 
            ? walletTransactions.isLoadingSpark || walletTransactions.isLoadingInternal
            : walletTransactions.isLoadingInternal;
    }, [walletType, walletTransactions.isLoadingSpark, walletTransactions.isLoadingInternal]);

    const error = useMemo(() => {
        return walletType === WalletType.SPARK 
            ? walletTransactions.errorSpark || walletTransactions.errorInternal
            : walletTransactions.errorInternal;
    }, [walletType, walletTransactions.errorSpark, walletTransactions.errorInternal]);

    const hasMore = useMemo(() => {
        return walletType === WalletType.SPARK 
            ? walletTransactions.hasMoreSpark || walletTransactions.hasMoreInternal
            : walletTransactions.hasMoreInternal;
    }, [walletType, walletTransactions.hasMoreSpark, walletTransactions.hasMoreInternal]);

    return {
        transactions: filteredTransactions,
        pendingTransactions: filteredPendingTransactions,
        isLoading,
        error,
        hasMore,
        loadTransactions,
        refreshPendingDeposits,
        getTransaction,
        getSearchTransaction,
        refreshTransaction,
        refreshLastTransactions,
        nbTxUnread,
        refreshPendingTransactions,
        cleanupPendingTransactions,
        unsetWalletTransactions,
        resetTransactions: useCallback(() => dispatch(resetTransactions()), [dispatch]),
        addTransaction: useCallback((transaction: Transaction) => 
            dispatch(addTransaction(transaction)), [dispatch]),
        addOrReplaceTransaction: useCallback((transaction: Transaction) => 
            dispatch(addOrReplaceTransaction(transaction)), [dispatch]),
        markTransactionAsRead: markTransactionAsReadHook,
        markTransactionAsAnnounced: markTransactionAsAnnouncedHook,
        markAllTransactionsAsRead: markAllTransactionsAsReadHook,
    };
}
